declare var angular: ng.IAngularStatic;

import { DialogsService, LongWorkService } from "../providers/uikit.dialogs";
import { AppContext } from "../services/context";
import { FileDescriptionDto, NamedEntityDto, UploadLimits } from "../models/common.model";
import { BaseRepository } from "approot/global/repository/baseRepository";
import { CommonXhrErrorHandler } from "webroot/static/src/common/js/common.error";
import { ChangeTracker } from "../changeTracker";
import { UrlHelper } from "webroot/static/src/common/js/urlHelper";
import { AlertsService } from "../providers/uikit.alerts";
import { EventEmitter } from "approot/global/models/common.reactive";
import { DownloadService } from "../common.module";
import { SettingsProvider } from "approot/global/settingsProvider";

export interface FileAttachmentsData {
	files: File[];
	context: any;
}

export interface FileAttachmentsOptions {
	multiple: boolean;
	maxFileCount?: number;
	showDescription: boolean;
	readonly?: boolean;
	filesExtensions?: (uploadLimits: UploadLimits) => string;
	attachmentTypes?: NamedEntityDto[];
	onSuccessAttach?: (file: FileInfo) => void;
	onSuccessDetach?: (file: FileInfo) => void;
	uploadLimits?: UploadLimitsLocal;
	uploadUrl?: string;
	imageFile?: boolean;
	needResize?: (fileSize: number, uploadLimits: UploadLimits) => boolean;
	maxWidth?: (uploadLimits: UploadLimits) => number;
	quality?: (uploadLimits: UploadLimits) => number;
	setWasChanged?: boolean;
	// переход с локальных uploadLimits на функции определения лимита
	sizeLimit?: (uploadLimits: UploadLimits) => number;
	hint?: boolean;
	confirmMessage?: string;
	compact?: boolean;
}

export interface FileAttachmentsSettings {
	data: FileAttachmentsData;
	options: FileAttachmentsOptions;
}

export interface File {
	attachmentGuid: string;
	name: string;
	description?: string;
	attachmentType?: number;
	//может ли быть удален из базы
	isCanDeleteFromDb?: boolean
}

export interface UserInfoFile extends File {
	userInfoAttachmentGuid: number,
	userInfoUserId: number,
	userInfoAttachmentType: number
}

export enum UserInfoAttachmentType {
    AccountAttachment = 1,
    ConsentData = 11,
    LargeFamilyCertificate = 12,
    BirthChildrenCertificate = 13,
    EldestChildEducInformation = 14
}

export interface UploadLimitsLocal {
	fileSizeLimit: number;
}

export interface FileUploadResult<T> {
	result: T;
	fileName: string;
	description: string;
}

export interface FileInfo extends File {
	//привязан ли к сущности в базе
	isNew: boolean;
}

export class FileAttachments {
	public files: FileInfo[];

	constructor(private rawFiles: File[]) {
		this.files = this.files || [];
		this.rawFiles = this.rawFiles || [];
		this.mapFiles();
	}

	private mapFiles() {
		this.files = this.rawFiles.map((rawFile) => {
			return <FileInfo>{
				name: rawFile.name,
				description: rawFile.description || "",
				attachmentType: rawFile.attachmentType,
				isNew: false,
				isCanDeleteFromDb: rawFile.isCanDeleteFromDb,
				attachmentGuid: rawFile.attachmentGuid
			}
		});
	}

	add(file: FileInfo) {
		this.files.push(file);
		this.files = this.files.sort((a, b) => {
			if (a.attachmentType > b.attachmentType) {
				return 1;
			}
			else if (a.attachmentType < b.attachmentType) {
				return -1;
			}
			else {
				if (a.name > b.name) {
					return 1;
				}
				else if (a.name < b.name) {
					return -1;
				}
				return 0;
			}
		});
	}

	removeByGuid(guids: string | string[]) {
		if (Array.isArray(guids)) {
			this.files = this.files.filter((f) => guids.indexOf(f.attachmentGuid) === -1);
		}
		else {
			this.files = this.files.filter((f) => f.attachmentGuid !== guids);
		}
	}

	exist(): boolean {
		return !!this.files.length
	}

	current(): FileInfo {
		return this.files[0]
	}

	getByGuid(guid: string) {
		// if not id then return
		return this.files.find((f) => f.attachmentGuid === guid);
	}
}

export class FileUploadService<T> {
	
	constructor(
		public uploadUrl: string,
		public extensions?: (uploadLimits: UploadLimits) => string,
		public uploadLimits?: UploadLimitsLocal,
		public imageFile?: boolean,
		// переход с локальных uploadLimits на функции определения лимита
		public sizeLimit?: (uploadLimits: UploadLimits) => number
	) {
	}

	// fileSize в КБ
	public needResize?: (fileSize: number, uploadLimits: UploadLimits) => boolean;
	public withPreview?: boolean;
	public maxWidth?: (uploadLimits: UploadLimits) => number;
	public quality?: (uploadLimits: UploadLimits) => number;
	public hint?: boolean;

	public queryStringParams?: any;
	public submit: () => Promise<FileUploadResult<T>>;
	public getSubmitData?: () => any;
	public onFileUploadDone?: (data: FileUploadResult<T>) => any;
	public onFileUploadFail?: (data: any) => any;
	public fileReady: () => boolean;
}

/*@ngInject*/
export function FileUploadDirective<T>(appContext: AppContext, $dialogs: DialogsService, language: any, $longWork: LongWorkService, Upload: ng.angularFileUpload.IUploadService, settingsProvider: SettingsProvider) {
	return {
		restrict: 'E',
		scope: {
			service: "=",
			withDescription: "=?"
		},
		template: `
			<div class="form-group" ng-if="service.imageFile && service.withPreview && previewFileSrc" style="text-align: center;">
				<img ng-src="{{previewFileSrc}}" alt="{{language.Generic.Common.kPreview}}" style="max-width: 400px; max-height: 400px;">
			</div>
			<div class="form-group">
				<div class='alert alert-info'  ng-if='service.hint'>
					{{language.Generic.Common.kFileImageSizeFileType}} {{service.uploadLimits.fileSizeLimit / 1024}} {{language.Generic.Common.kMB}}
				</div>
				<div class="input-group">
					<span class="btn btn-primary btn-file input-group-addon">
						{{language.Generic.Common.kSelectFile}} 
						<input track-changes type="file" id="file" ngf-select ngf-model-options="{updateOn: 'change drop dropUrl paste', allowInvalid: false, debounce: 0}" ng-model="file" ngf-change="uploadChange($file)" name="fileAttachment" class="form-control" accept="{{acceptFiles}}">
					</span>
					<input type="text" class="form-control file-input-filename" id="fileName" name="fileName" ng-model="filename" readonly>
				</div>
			</div>
			<div class="form-group" ng-if="withDescription">
				<label class="control-label">{{language.Generic.Curriculum.kDescription}}</label>
				<textarea class="form-control" rows="10" ng-model="$parent.description" maxlength="4000" name="description" style="border: 1px solid #8baed8;"></textarea>
			</div>`,
		link: (scope) => {
			scope.language = language;
			settingsProvider.UploadLimits.then((result: UploadLimits) => {
				scope.uploadLimits = result

				let service = <FileUploadService<T>>scope.service;

				// тут должна отрабатывать функция определения лимита
				// желательно впоследствии сделать безусловным и избавиться от локальных uploadLimits
				if(!service.uploadLimits){
					// если произошёл переход на функцию лимита
					if(service.sizeLimit){
						service.uploadLimits = {fileSizeLimit: service.sizeLimit(scope.uploadLimits)};
					}
					else{
						// если ограничение не выставлено ни в каком виде - значение по умолчанию
						service.uploadLimits = {fileSizeLimit: scope.uploadLimits.fileSizeLimit};
					}
				}

				scope.file = {};
				scope.description = "";

				scope.uploadChange = ($file) => {
					scope.file = {};
					scope.filename = "";
					if ($file) {
						scope.file = $file;
						scope.filename = $file.name;

						if (service.imageFile && service.withPreview) {
							let prepareFile: ng.IPromise<globalThis.File> = Promise.resolve(scope.file);
							if (service.needResize && service.needResize(scope.file.size / 1024, scope.uploadLimits)) {
								prepareFile = resize(scope.file);
							}

							prepareFile.then(file => {
								var reader = new FileReader();
								reader.onload = function () {
									scope.previewFileSrc = reader.result;
								}
								reader.readAsDataURL(file);
							})
						}
					}
				}
				
				
				if (service.extensions && service.extensions(scope.uploadLimits).length) {
					let extensions = service.extensions(scope.uploadLimits).replace(/\s/g, "").split(",");
					scope.acceptFiles = extensions.reduce((msg, ext, ind) => msg += (ind > 0 ? "," : "") + (ext.startsWith('.') ? "" : ".") + ext, "");
				}
				else if (service.imageFile) {
					scope.acceptFiles = "image/*";
				}

				let eventEmmit = new EventEmitter<FileUploadResult<T>>();

				const prepareUpload = () => {
					let url = service.uploadUrl;
					if (service.queryStringParams) {
						url = new UrlHelper().makeUrl(service.uploadUrl, service.queryStringParams, true)
					}

					let extraData: any = null;
					if (scope.withDescription && scope.description) {
						extraData = { description: scope.description };
					}

					if (service.getSubmitData) {
						extraData = angular.extend({}, service.getSubmitData(), extraData || {});
					}

					let data = {
						name: scope.filename
					};

					if (extraData) {
						data = angular.extend(data, extraData);
					}

					let document = {
						file: scope.file,
						data: JSON.stringify(data)
					}

					let config: ng.angularFileUpload.IFileUploadConfigFile = {
						url: url,
						method: "POST",
						data: document
					}

					$longWork.show();
					uploadFile(config);
				};

				let handleSuccess = (response) => {
					$longWork.close();
					if (response.status != 204 && !response.data) {
						$dialogs.error(language.Generic.Common.kUnexpErr);
						return;
					}

					let uploadResults: FileUploadResult<T> = {
						result: <T>response.data,
						fileName: scope.filename,
						description: scope.description
					};

					if (service.onFileUploadDone) {
						service.onFileUploadDone(uploadResults);
					}
				};

				let handleError = (response) => {
					$longWork.close();

					if (service.onFileUploadFail) {
						service.onFileUploadFail(response.data);
					}

					const errInformer = (message) => $dialogs.error(message);
					const messageInformer = (message) => $dialogs.message(message);
					return new CommonXhrErrorHandler(errInformer, messageInformer, () => appContext, language).handleErrorResponse(response);
				};

				const uploadFile = (config: ng.angularFileUpload.IFileUploadConfigFile) => {
					return Upload.upload(config).then(handleSuccess, handleError);
				}

				const checkImage = (fileObject: any) => new Promise<boolean>(resolve => {
					const img = new Image();
					var objUrl;
					try {
						objUrl = URL.createObjectURL(fileObject);
					}
					catch (e) {
						return resolve(true);
					}

					img.onload = () => {
						try {
							URL.revokeObjectURL(objUrl);
						}
						catch (e) {
							return resolve(true);
						}
						return resolve(true)
					};

					img.onerror = () => {
						try {
							URL.revokeObjectURL(objUrl);
						}
						catch (e) {
							return resolve(false);
						}
						return resolve(false)
					};

					try {
						img.src = objUrl;
					}
					catch (e) {
						return resolve(true);
					}
				});


				service.onFileUploadDone = (data: FileUploadResult<T>) => {
					eventEmmit.emit(data);
				}

				service.onFileUploadFail = (data: any) => {
					if ($longWork.isShowing) {
						$longWork.close();
					}
					eventEmmit.emit(null);
				}

				let resize = (file: globalThis.File) => {
					let fileResizeOptions: ng.angularFileUpload.FileResizeOptions = {};
					if (service.quality) {
						fileResizeOptions.quality = service.quality(scope.uploadLimits);
					}
					if (service.maxWidth) {
						fileResizeOptions.width = service.maxWidth(scope.uploadLimits);
					}
					return Upload.resize(file, fileResizeOptions);
				}

				let validateSubmit = () => new Promise<ValidateInfo>((resolve) => {
					var defaultStrings = $dialogs.getDefaultStrings();
					var okMessage = defaultStrings.ok;
					if (!scope.file || !scope.filename) {
						$dialogs.translate({ ok: language.Generic.Common.kOk })
						$dialogs.message(language.Generic.Common.kMustSelectFile).then(() => {
							$dialogs.translate({ ok: okMessage });
						},
							() => {
								$dialogs.translate({ ok: okMessage });
							});
						return resolve({canSubmit: false, errorInfo: "noFile"});
					}

					
					if (service.extensions && service.extensions(scope.uploadLimits).length) {
						let extensions = service.extensions(scope.uploadLimits).replace(/\s/g, "").split(",");
						let isExtensionsCorrect = extensions.some((ext) => scope.filename.toLowerCase().endsWith(ext));
						if (!isExtensionsCorrect) {
							let allowedExtMsg = language.Generic.Common.kAllowedExtentionsToDownLoad + extensions.reduce((msg, ext) => msg += ", " + ext);
							$dialogs.translate({ ok: language.Generic.Common.kOk })
							$dialogs.message(`${language.Generic.Common.kInvalidFileExtension}\n` + allowedExtMsg).then(() => {
								$dialogs.translate({ ok: okMessage });
							},
								() => {
									$dialogs.translate({ ok: okMessage });
								});

							return resolve({canSubmit: false, errorInfo: "ext"});
						}
					}

					let prepareFile: ng.IPromise<globalThis.File> = Promise.resolve(scope.file);

					$longWork.show();

					const imageExts = ['.tiff', '.pjp', '.jfif', '.bmp', '.gif', '.svg', '.png',
									   '.xbm', '.dlib', '.jxl', '.jpeg', '.svgz', '.jpg', '.webp',
									   '.ico', '.tif', '.pjpeg', '.avif'];
					let needResize = service.needResize && service.needResize(scope.file.size / 1024, scope.uploadLimits);
					let isImage = imageExts.some((ext) => scope.filename.toLowerCase().endsWith(ext));
					if (isImage && needResize) {
						prepareFile = resize(scope.file);
					}

					prepareFile.then((file) => {
						$longWork.close();

						scope.file = file;

						//общие ограничения на загрузку
						if (scope.file.size && service.uploadLimits.fileSizeLimit && scope.file.size > service.uploadLimits.fileSizeLimit * 1024) {
							$dialogs.translate({ ok: language.Generic.Common.kOk })
							let limitSize;
							if (service.uploadLimits.fileSizeLimit > 1024) {
								limitSize = service.uploadLimits.fileSizeLimit / 1024 + language.Generic.Common.kMB;
							}
							else {
								limitSize = service.uploadLimits.fileSizeLimit + ` ${language.Generic.Common.kKb}`;
							}

							$dialogs.error(language.Generic.LearnApp.kServErrFileTooLarge + limitSize)
								.finally(() => {
									$dialogs.translate({ ok: okMessage });
								});
							return resolve({canSubmit: false, errorInfo: "size"});
						}

						if (service.imageFile) {
							return checkImage(scope.file)
								.then((validImage) => {
									if (validImage) {
										return resolve({canSubmit: true, errorInfo: ""});
									}
									else {
										$dialogs.translate({ ok: language.Generic.Common.kOk })
										$dialogs.message(language.Generic.Common.kUpLoadFileNotImageEmpty)
											.finally(() => {
												$dialogs.translate({ ok: okMessage });
											});
										return resolve({canSubmit: false, errorInfo: "noImage"});
									}
								});
						}

						return resolve({canSubmit: true, errorInfo: ""});

					})
				});

				service.submit = () => {
					return validateSubmit()
						.then((canSubmitInfo) => new Promise<FileUploadResult<T>>((resolve, reject) => {
							if (canSubmitInfo.canSubmit) {
								prepareUpload();
								eventEmmit.off();
								eventEmmit.on((uploadResult: FileUploadResult<T>) => {
									//см. onFileUploadFail
									if (uploadResult == null) {
										reject()
									}
									else {
										resolve(uploadResult);
									}

								});
							}
							else {
								reject(canSubmitInfo.errorInfo);
							}
						}));

				}

				service.fileReady = () => {
					return scope.file 
					? scope.file.name ? true : false 
					: false;
				}
			});
			
		}
	}
}

class EditAttachmentController {
	public fileUploadService: FileUploadService<string>;

	public attachmentType?: NamedEntityDto;
	public header: string;

	public withTypes: boolean;

	public mode = {
		attach: false,
		edit: false
	}

	/*@ngInject*/
	constructor(
		private $uibModalInstance: ng.ui.bootstrap.IModalInstanceService,
		private $alerts: AlertsService,
		private attachmentsRepository: AttachmentsRepository,
		private changeTracker: ChangeTracker,
		public language: any,

		public file: FileInfo,
		public withDescription: boolean,
		public fileExtensions?: (uploadLimits: UploadLimits) => string,
		public uploadUrl?: string,
		public uploadLimits?: UploadLimitsLocal,
		public context?: any,
		public attachmentTypes?: NamedEntityDto[],
		public imageFile?: boolean,
		public needResize?: (fileSize: number, uploadLimits: UploadLimits) => boolean,
		public maxWidth?: (uploadLimits: UploadLimits) => number,
		public quality?: (uploadLimits: UploadLimits) => number,
		public setWasChanged?: boolean,
		public sizeLimit?: (uploadLimits: UploadLimits) => number,
		public hint?: boolean
		) 
		{

		if (this.file.attachmentGuid) {
			this.mode.edit = true;
			this.header = language.Generic.Common.kEditAttachmentDescription;
		}
		else {
			this.header = language.Generic.Buttons.kAttachFile;
			this.mode.attach = true;

			const uploadService = new FileUploadService<string>(uploadUrl || '/webapi/attachments', this.fileExtensions, this.uploadLimits, this.imageFile, this.sizeLimit);

			uploadService.needResize = needResize;
			uploadService.maxWidth = maxWidth;
			uploadService.quality = quality;
			uploadService.hint = hint;

			uploadService.getSubmitData = () => {
				let extraData = Object.assign({
					description: this.file.description,
					attachmentType: this.attachmentType && this.attachmentType.id
				}, this.context);

				return extraData;
			};

			this.fileUploadService = uploadService;
		}

		this.withTypes = this.attachmentTypes && this.attachmentTypes.length > 0;

		if (this.file.attachmentType && this.attachmentTypes) {
			this.attachmentType = this.attachmentTypes.find((t) => t.id == this.file.attachmentType);
			if (this.attachmentType && this.file?.description?.indexOf(`(${this.attachmentType.name}) `) == 0) {
				this.file.description = this.file.description.slice(this.attachmentType.name.length + 3);
			}
		}
		else if (this.withTypes) {
			this.attachmentType = this.attachmentTypes[0];
		}
	}

	attach() {
		this.fileUploadService.submit()
			.then(uploadResult => {
				if (!uploadResult) {
					return;
				}

				let description = uploadResult.description;
				if (this.attachmentType) {
					description = "(" + this.attachmentType.name + ") " + description;
				}

				let file: FileInfo = {
					name: uploadResult.fileName,
					description: description,
					attachmentType: this.attachmentType && this.attachmentType.id,
					isNew: true,
					attachmentGuid: uploadResult.result
				}

				if (this.setWasChanged !== false) {
					this.changeTracker.dataWasChanged();
				}
				this.$alerts.success(this.language.Generic.Common.kFileSuccesLoad);
				this.$uibModalInstance.close(file);
			},
				() => { });
	}

	save() {
		var descriptionInfo: FileDescriptionDto = { attachmentType: this.attachmentType?.id,  fileDescription: this.file.description };
		this.attachmentsRepository.editDescription(this.file.attachmentGuid, descriptionInfo)
			.then((result) => {
				this.$alerts.success(this.language.Generic.Common.kDescriptSuccesChange);
				this.file.description = result?.fileDescription;
				this.file.attachmentType = result?.attachmentType;
				this.$uibModalInstance.close(this.file);
			});
	}

	cancel() {
		this.$uibModalInstance.dismiss("cancel")
	}
}

export class FileAttachmentsServiceProvider {
	/*@ngInject*/
	constructor(
		public language: any,
		private $uibModal: ng.ui.bootstrap.IModalService,
		private attachmentsRepository: AttachmentsRepository,
		private $dialogs: DialogsService,
		public $longWork: LongWorkService
	) {
	}

	getEditAttachmentsService(options: FileAttachmentsOptions, context?: any) {
		return new EditAttachmentService(this.language, this.$uibModal, this.attachmentsRepository, this.$dialogs, options, this.$longWork, context);
	}
}

export class EditAttachmentService {

	constructor(
		public language: any,
		private $uibModal: ng.ui.bootstrap.IModalService,
		private attachmentsRepository: AttachmentsRepository,
		private $dialogs: DialogsService,
		private options: FileAttachmentsOptions,
		public $longWork: LongWorkService,
		private context?: any,
	) {

	}

	attachFile(): Promise<FileInfo> {
		let file: FileInfo = {
			attachmentGuid: "",
			name: "",
			description: "",
			isNew: true
		};

		var modalInstance = this.$uibModal.open({
			template: EditAttachmentComponent.template,
			controller: EditAttachmentComponent.controller,
			controllerAs: "$ctrl",
			resolve: {
				file: () => file,
				context: () => this.context,
				fileExtensions: () => this.options.filesExtensions,
				uploadUrl: () => this.options.uploadUrl,
				uploadLimits: () => this.options.uploadLimits,
				withDescription: () => this.options.showDescription,
				attachmentTypes: () => this.options.attachmentTypes,
				imageFile: () => this.options.imageFile,
				needResize: () => this.options.needResize,
				maxWidth: () => this.options.maxWidth,
				quality: () => this.options.quality,
				setWasChanged: () => this.options.setWasChanged,
				sizeLimit: () => this.options.sizeLimit,
				hint: () => this.options.hint
			}
		});

		let promise = <Promise<FileInfo>>modalInstance.result;

		promise.then((file: FileInfo) => {
			if (this.options.onSuccessAttach) {
				this.options.onSuccessAttach(file);
			}
		});

		return promise;
	}

	editDescriptionFile(file: FileInfo): Promise<FileInfo> {
		var modalInstance = this.$uibModal.open({
			template: EditAttachmentComponent.template,
			controller: EditAttachmentComponent.controller,
			controllerAs: "$ctrl",
			resolve: {
				file: () => Object.assign({}, file),
				context: () => this.context,
				fileExtensions: () => this.options.filesExtensions,
				uploadLimits: () => this.options.uploadLimits,
				withDescription: () => this.options.showDescription,
				attachmentTypes: () => this.options.attachmentTypes,
				imageFile: () => this.options.imageFile,
				needResize: () => this.options.needResize,
				maxWidth: () => this.options.maxWidth,
				quality: () => this.options.quality,
				sizeLimit: () => this.options.sizeLimit,
				hint: () => this.options.hint,
				setWasChanged: () => this.options.setWasChanged,
				uploadUrl: () => this.options.uploadUrl
			}
		});

		let promise = <Promise<FileInfo>>modalInstance.result;

		promise.then((file: FileInfo) => {
			if (this.options.onSuccessAttach) {
				this.options.onSuccessAttach(file);
			}
		});

		return promise;
	}

	delete(file: FileInfo): Promise<void> {
		const defaultMessage = `${this.language.Generic.Buttons.kDelFile} ` + file.name + "?";

		const message = this.options.confirmMessage
			? this.options.confirmMessage
			: defaultMessage;

		return this.$dialogs.confirmDelete(message)
			.then(() => {
				//todo. процессинг
				this.$longWork.show();
				return this.attachmentsRepository.delete(file.attachmentGuid, this.context);
			})
			.then(() => {
				this.$longWork.close();
				if (this.options.onSuccessDetach) {
					this.options.onSuccessDetach(file);
				}
			})
			.catch((error) => {
				this.$longWork.close();
				return Promise.reject(error);
			});
	}
}

const EditAttachmentComponent = {
	controller: EditAttachmentController,
	template: `
			<div class="bootstrap-dialog type-primary">

			<div class="modal-header">
				<div class="bootstrap-dialog-title">
					<h4 class="modal-title">{{$ctrl.header}}</h4>
				</div>
				<div class="bootstrap-dialog-close-button">
					<button class="close" ng-click="$ctrl.cancel()">×</button>
				</div>
			</div>

			<div class="modal-body">
				<form name="form" method="POST" enctype="multipart/form-data">

				<div class="form-group" ng-if="$ctrl.withTypes">
					<div>
						<label for="attachmentType" class="control-label">{{$ctrl.language.Generic.Common.kAttachType}}</label>
					</div>
					<div>
						<select class="form-control" 
							name="attachmentType" 
							ng-model="$ctrl.attachmentType" 
							ng-options="type as type.name for type in $ctrl.attachmentTypes track by type.id">
						</select>
					</div>
				</div>

				<div class="form-group" ng-if="$ctrl.mode.attach">
					<file-upload service="$ctrl.fileUploadService" with-description="$ctrl.withDescription"></file-upload>
				</div>

				<div class="form-group" ng-if="$ctrl.withDescription && $ctrl.mode.edit">
					<label class="control-label">{{$ctrl.language.Generic.Curriculum.kDescription}}</label>
					<textarea class="form-control" ng-model="$ctrl.file.description" rows="10" maxlength="4000" name="description" style="border: 1px solid #8baed8;"></textarea>
				</div>
			</form>
		</div>

	<div class="modal-footer">

		<button class="btn btn-primary btn-sm" ng-click="$ctrl.attach()" ng-if="$ctrl.mode.attach">
			<span class="glyphicon glyphicon-paperclip"></span> 
			<span>{{$ctrl.language.Generic.Buttons.kAttachFile}}</span>
		</button>

		<button class="btn btn-primary btn-sm" ng-click="$ctrl.save()" ng-if="$ctrl.mode.edit">
			<span class="glyphicon glyphicon-floppy-save"></span>
			<span>{{$ctrl.language.Generic.Buttons.kSave}}</span>
		</button>

		<button class="btn btn-default btn-sm" ng-click="$ctrl.cancel()">
			<span class="bootstrap-dialog-button-icon glyphicon glyphicon-ban-circle"></span>
			<span>{{$ctrl.language.Generic.Buttons.kCancel}}</span>
		</button>

	</div>
</div>
`
}

export class AttachmentsRepository extends BaseRepository {
	delete(fileGuid: string, context?: any): Promise<any> {
		return this.$http.post('/webapi/attachments/' + fileGuid + "/delete", context)
			.then(this.handleResponse, this.handleError);
	}

	editDescription(fileGuid: string, fileDescription: FileDescriptionDto) {
		return this.$http.post('/webapi/attachments/' + fileGuid + '/description', fileDescription)
			.then(this.handleResponse, this.handleError);
	}
}

class FileAttachmentCtrl {

	public fileAttachments: FileAttachments;
	public settings: FileAttachmentsSettings;
	private editService: EditAttachmentService;

	/*@ngInject*/
	constructor(
		public language: any,
		private $scope: ng.IScope,
		private downloadService: DownloadService,
		private fileAttachmentsServiceProvider: FileAttachmentsServiceProvider,
		private $alerts: AlertsService,
		private $longWork: LongWorkService,
	) {

	}

	$onInit() {
		if (this.settings && this.settings.data) {
			this.fileAttachments = new FileAttachments(this.settings.data.files || []);
			this.editService = this.fileAttachmentsServiceProvider.getEditAttachmentsService(this.settings.options, this.settings.data.context);
		}
		this.$scope.$watch(() => this.settings && this.settings.data && this.settings.data.files, () => {
			if (this.settings && this.settings.data) {
				this.editService = this.fileAttachmentsServiceProvider.getEditAttachmentsService(this.settings.options, this.settings.data.context);
				this.fileAttachments = new FileAttachments(this.settings.data.files || []);
			}
		});
	}

	openAttachment(file: FileInfo) {
		let downloadUrl = "/webapi/attachments/" + file.attachmentGuid;
		this.$longWork.show();
		this.downloadService.downloadFile(downloadUrl, { filename: file.name }).then(
			() => this.$longWork.close(),
			() => this.$longWork.close());
	}

	attachFile() {
		this.editService.attachFile()
			.then(file => {
				this.fileAttachments.add(file);
				this.settings.data.files = this.fileAttachments.files;
			});
	}

	editDescription(file: FileInfo) {
		this.editService.editDescriptionFile(file)
			.then((editedFile) => {
				var file = this.fileAttachments.getByGuid(editedFile.attachmentGuid);
				file.description = editedFile.description;
				file.attachmentType = editedFile.attachmentType;
				this.settings.data.files = this.fileAttachments.files;
				this.$scope.$applyAsync();
			});
	}

	deleteFile(file: FileInfo) {
		if (typeof file.isCanDeleteFromDb !== "undefined" && !file.isCanDeleteFromDb) {
			this.fileAttachments.removeByGuid(file.attachmentGuid);
			this.settings.options.onSuccessDetach(file);
			return
		}

		this.editService.delete(file)
			.then(() => {
				this.fileAttachments.removeByGuid(file.attachmentGuid);
				this.settings.data.files = this.fileAttachments.files;
				this.$alerts.success(this.language.Generic.Messages.kFileSuccDel);
				this.$scope.$applyAsync();
			});
	}

	showAddButton(){
		const notReadonly = !this.settings?.options?.readonly;
		const multipleOption = this.settings?.options?.multiple;
		const countRestriction = this.settings?.options?.maxFileCount;
		const fileCount = this.fileAttachments?.files?.length;
		const noFiles = !this.fileAttachments?.files?.length;

		const multipleNotRestricted = multipleOption && !countRestriction;
		const multipleCountValid = multipleOption && fileCount < countRestriction;

		return notReadonly && (multipleNotRestricted || noFiles || multipleCountValid);
	}
}

class ValidateInfo {
	public canSubmit: boolean;
	public errorInfo: string;
}

export const FileAttachmentsComponent = {
	controller: FileAttachmentCtrl,
	bindings: {
		settings: "=",
	},
	template: `
		<div class="file-attachment-block" ng-class="{'multiple': $ctrl.settings.options.multiple}">
			<div ng-repeat="file in $ctrl.fileAttachments.files" class="file-attachment" title="{{file.name + $ctrl.settings.options.showDescription ? ' ' + file.description : ''}}">
				<div class="file-info" ng-click="$ctrl.openAttachment(file);">
					<span class="file-name">{{file.name}}</span>
					<span class="file-description" ng-if="$ctrl.settings.options.showDescription && file.description">{{file.description}}</span>
				</div>
				<div id="{{file.id}}" class="buttons" ng-if="!$ctrl.settings.options.readonly">
					<span ng-click="$ctrl.editDescription(file)" ng-if="$ctrl.settings.options.showDescription" class="glyphicon glyphicon-pencil edit-description-button" aria-hidden="true" title="{{$ctrl.language.Generic.Buttons.kEditDescript}}"></span>
					<span ng-click="$ctrl.deleteFile(file)" class="glyphicon glyphicon-trash remove-button" aria-hidden="true" title="{{$ctrl.language.Generic.Buttons.kDelFile}}"></span>
				</div>
			</div>
			<button ng-if="$ctrl.showAddButton()" class="btn btn-sm" ng-class="$ctrl.settings.options.compact ? 'btn-link' : 'btn-default'" type="button" title="{{$ctrl.language.Generic.Buttons.kAttachFile}}" ng-click="$ctrl.attachFile()">
				<span class="glyphicon glyphicon-paperclip"></span>
				<span ng-if="!$ctrl.settings.options.compact">{{$ctrl.language.Generic.Buttons.kAttachFile}}</span>
			</button>
		</div>`
}
